/**
 * Operator-facing LABELS for Studio chat operations.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS MODULE EXISTS — the field split
 * ---------------------------------------------------------------------------
 * `studio-chat-operation-registry.ts` used to carry ONE `label` and ONE
 * `description` per operation, read by two audiences at once:
 *
 *   1. The MODEL. `formatStudioChatOperationInstructions` serializes them into
 *      `chatOperationInstructions`, which `chat-model-adapter.ts` and
 *      `gateway-chat-service.ts` put on the wire.
 *   2. The OPERATOR, through render sinks (`ask-cogo-surface-button.tsx`,
 *      `conversation-target-summary.tsx`, `studio-task-orchestrator.ts`, the
 *      capability gateway's pending labels).
 *
 * Translating the registry field in place would have changed what the model
 * reads. So the field is split, the same way thrown errors already are — a
 * stable identifier at the source, a locale table at the render sink:
 *
 *   registry `label`/`description`  ->  ENGLISH, prompt channel, never localized
 *   this module, keyed by operation id  ->  what a human sees
 *
 * `studio-chat-operation-prompt-invariance.test.ts` holds the prompt channel to
 * byte-equality against a baseline emitted before the split. If a translation
 * ever leaks back into the registry, that gate fails.
 *
 * ---------------------------------------------------------------------------
 * WHY THE LABELS ARE HERE AND THE DESCRIPTIONS ARE NOT
 * ---------------------------------------------------------------------------
 * The descriptions moved to `studio-operation-description-copy.ts`, which is
 * reached ONLY through `loadStudioOperationDescription` below — a dynamic
 * import, so those tables are their own chunk instead of initial payload.
 *
 * The labels could NOT follow them, and the reason is worth keeping written
 * down because the opposite was assumed once:
 *
 *   Labels are read DURING RENDER by surfaces that exist with no operation card
 *   anywhere. `ask-cogo-surface-button.tsx` renders one on every IDE surface
 *   toolbar — `behavior-surface.tsx` ships `label="Review behavior"` with
 *   `operationId="behavior.surface.review"`, whose English table entry is
 *   exactly "Review behavior", so `resolveStudioOperationButtonLabel`
 *   localizes it. `ask-cogo-surface-button-i18n.test.tsx` asserts a standalone
 *   button renders "페이지 이름 변경" synchronously, with no chat thread mounted.
 *   Making the labels async would paint the caller's ENGLISH `label` prop and
 *   then swap it to Korean.
 *
 *   Descriptions have exactly one production reader — the notice composed in
 *   `askCogo`, a CLICK HANDLER. Nothing paints a description, so awaiting one
 *   introduces no swap.
 *
 * ---------------------------------------------------------------------------
 * NOT LOCALIZED, deliberately — asserted in the companion test
 * ---------------------------------------------------------------------------
 *   - Operation IDS (`page.details.rename`, …) are schema vocabulary. They are
 *     the key of every table below and are verbatim in all five locales.
 *   - Tool names (`cogo.studio.updatePageMeta`) — WebMCP/Studio MCP vocabulary.
 *   - Product surface names Coogo does not translate (Coogo, Figma, GitHub).
 *
 * The `en` table is the text operators saw BEFORE the split; it is pinned
 * against the live registry by the companion test, so a registry rename shows
 * up here rather than silently diverging.
 */
import { useStudioLocale } from "@/i18n";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
import type { StudioOperationId } from "./studio-operation-v2-profiles";

type OperationLabelTable = Record<StudioOperationId, string>;

const EN: OperationLabelTable = {
  "workspace.semantic-sync": "Sync one change everywhere",
  "page.details.review": "Review page",
  "page.details.rename": "Rename page",
  "page.details.route": "Change route",
  "page.details.blocks": "Review blocks",
  "page.details.layout": "Adjust layout",
  "page.details.delete": "Delete page",
  "element.add": "Add element",
  "element.update": "Update element",
  "element.remove": "Remove element",
  "behavior.surface.review": "Review behavior",
  "behavior.flow.review": "Review flow",
  "flow.create": "Create flow",
  "flow.rename": "Rename flow",
  "behavior.flow.add-step": "Add flow step",
  "behavior.flow.connect-selected": "Connect selected element",
  "event-binding.remove": "Remove event binding",
  "behavior.flow.test": "Test flow",
  "behavior.flow.delete": "Delete flow",
  "data.surface.review": "Review data",
  "evidence.checks.review": "Review checks",
  "evidence.candidate.read": "Read candidate verification",
  "evidence.sufficiency.read": "Read source sufficiency",
  "evidence.sufficiency.answer": "Answer source clarification",
  "evidence.knowledge.search": "Search approved knowledge",
  "variable.add": "Add variable",
  "variable.update": "Update variable",
  "variable.remove": "Remove variable",
  "data.source.add": "Add data source",
  "data.source.scaffold-from-chat": "Scaffold data source",
  "datasource.update": "Update data source",
  "data.binding.connect": "Connect data binding",
  "data.flow.debug": "Debug data flow",
  "data.source.remove": "Remove data source",
  "application.generate": "Generate application",
  "application.create-from-brief": "Create application from brief",
  "application.patch": "Apply application patch",
  "application.bundle.export": "Export Coogo bundle",
  "figma.source.import": "Import Figma source",
  "figma.token.validate": "Validate Figma token",
  "figma.frame.import": "Import Figma frame",
  "github.preview.import": "Import GitHub PR preview",
  "source.figma.inspect": "Inspect Figma source",
  "source.figma.export_original": "Export current Figma source",
  "source.figma.edit": "Edit Figma source",
  "source.figma.apply_design_mutation": "Apply coordinated design mutation",
  "source.figma.propose_design_mutation": "Prepare app + source edit",
  "source.figma.apply_mapping": "Apply source mapping",
  "source.figma.decide_tokens": "Decide design tokens",
  "source.figma.visual_review": "Run visual review",
  "source.figma.assemble": "Build source assembly",
  "source.figma.runtime_proof": "Run runtime proof",
  "source.figma.create_candidate": "Create Coogo candidate",
  "report.workspace.review": "Review report workspace",
  "report.document-page.review": "Review document page",
  "report.document-page.edit": "Edit document page",
  "report.document-page.validate": "Validate document page",
  "report.document-page.export": "Export document page",
  "report.export-target.validate": "Validate export target",
  "document.workspace.inspect": "Inspect document workspace",
  "document.page.inspect": "Inspect document page",
  "document.page.create": "Create document page",
  "document.page.update_node": "Update document content",
  "document.page.wordgard": "Open narrative draft",
  "document.page.move": "Move document page",
  "document.page.delete": "Delete document page",
  "document.page.export": "Export document page",
  "document.page.select_anchor": "Select document anchor",
  "document.page.propose_edit": "Propose document edit",
  "document.proposal.apply": "Apply document proposal",
  "document.proposal.list": "List document proposals",
  "document.proposal.reject": "Reject document proposal",
  "document.blocker.explain": "Explain document blocker",
  "document.target.export": "Export document target",
  "document.target.readback": "Inspect document export checks",
  "theme.preset.set": "Set theme preset",
  "theme.color.update": "Update theme color",
  "theme.font.set": "Set application font",
  "theme.radius.set": "Set application radius",
  "theme.reset": "Reset application theme",
  "project.create": "Create project",
  "project.open": "Open project",
  "project.close": "Close project",
  "project.rename": "Rename project",
  "project.metadata.update": "Update project metadata",
  "project.delete": "Delete project",
  "project.duplicate": "Duplicate project",
  "release.preview.share": "Share Coogo preview",
  "release.verify": "Run publication verification",
  "release.deploy": "Deploy publication release",
  "release.readback": "Refresh publication check",
  "release.rollback": "Rollback publication receipt",
  "project.publish.confirm": "Publish project",
};

const KO: OperationLabelTable = {
  "workspace.semantic-sync": "한 번의 변경을 전체에 동기화",
  "page.details.review": "페이지 검토",
  "page.details.rename": "페이지 이름 변경",
  "page.details.route": "경로 변경",
  "page.details.blocks": "블록 검토",
  "page.details.layout": "레이아웃 조정",
  "page.details.delete": "페이지 삭제",
  "element.add": "요소 추가",
  "element.update": "요소 수정",
  "element.remove": "요소 제거",
  "behavior.surface.review": "동작 검토",
  "behavior.flow.review": "플로우 검토",
  "flow.create": "플로우 생성",
  "flow.rename": "플로우 이름 변경",
  "behavior.flow.add-step": "플로우 스텝 추가",
  "behavior.flow.connect-selected": "선택한 요소 연결",
  "event-binding.remove": "이벤트 바인딩 제거",
  "behavior.flow.test": "플로우 테스트",
  "behavior.flow.delete": "플로우 삭제",
  "data.surface.review": "데이터 검토",
  "evidence.checks.review": "검사 검토",
  "evidence.candidate.read": "후보 검증 읽기",
  "evidence.sufficiency.read": "소스 충분성 읽기",
  "evidence.sufficiency.answer": "소스 확인 질문 답변",
  "evidence.knowledge.search": "승인된 지식 검색",
  "variable.add": "변수 추가",
  "variable.update": "변수 수정",
  "variable.remove": "변수 제거",
  "data.source.add": "데이터 소스 추가",
  "data.source.scaffold-from-chat": "데이터 소스 스캐폴딩",
  "datasource.update": "데이터 소스 수정",
  "data.binding.connect": "데이터 바인딩 연결",
  "data.flow.debug": "데이터 플로우 디버그",
  "data.source.remove": "데이터 소스 제거",
  "application.generate": "애플리케이션 생성",
  "application.create-from-brief": "브리프로 애플리케이션 생성",
  "application.patch": "애플리케이션 패치 적용",
  "application.bundle.export": "Coogo 번들 내보내기",
  "figma.source.import": "Figma 소스 가져오기",
  "figma.token.validate": "Figma 토큰 검증",
  "figma.frame.import": "Figma 프레임 가져오기",
  "github.preview.import": "GitHub PR 미리보기 가져오기",
  "source.figma.inspect": "Figma 소스 점검",
  "source.figma.export_original": "현재 Figma 소스 내보내기",
  "source.figma.edit": "Figma 소스 편집",
  "source.figma.apply_design_mutation": "연계 디자인 변경 적용",
  "source.figma.propose_design_mutation": "앱 + 소스 편집 준비",
  "source.figma.apply_mapping": "소스 매핑 적용",
  "source.figma.decide_tokens": "디자인 토큰 결정",
  "source.figma.visual_review": "시각 검토 실행",
  "source.figma.assemble": "소스 어셈블리 빌드",
  "source.figma.runtime_proof": "런타임 증명 실행",
  "source.figma.create_candidate": "Coogo 후보 생성",
  "report.workspace.review": "리포트 워크스페이스 검토",
  "report.document-page.review": "문서 페이지 검토",
  "report.document-page.edit": "문서 페이지 편집",
  "report.document-page.validate": "문서 페이지 검증",
  "report.document-page.export": "문서 페이지 내보내기",
  "report.export-target.validate": "내보내기 대상 검증",
  "document.workspace.inspect": "문서 워크스페이스 점검",
  "document.page.inspect": "문서 페이지 점검",
  "document.page.create": "문서 페이지 생성",
  "document.page.update_node": "문서 콘텐츠 수정",
  "document.page.wordgard": "서술 초안 열기",
  "document.page.move": "문서 페이지 이동",
  "document.page.delete": "문서 페이지 삭제",
  "document.page.export": "문서 페이지 내보내기",
  "document.page.select_anchor": "문서 앵커 선택",
  "document.page.propose_edit": "문서 편집 제안",
  "document.proposal.apply": "문서 제안 적용",
  "document.proposal.list": "문서 제안 목록",
  "document.proposal.reject": "문서 제안 거부",
  "document.blocker.explain": "문서 차단 요인 설명",
  "document.target.export": "문서 대상 내보내기",
  "document.target.readback": "문서 내보내기 검사 점검",
  "theme.preset.set": "테마 프리셋 설정",
  "theme.color.update": "테마 색상 수정",
  "theme.font.set": "애플리케이션 글꼴 설정",
  "theme.radius.set": "애플리케이션 모서리 반경 설정",
  "theme.reset": "애플리케이션 테마 초기화",
  "project.create": "프로젝트 생성",
  "project.open": "프로젝트 열기",
  "project.close": "프로젝트 닫기",
  "project.rename": "프로젝트 이름 변경",
  "project.metadata.update": "프로젝트 메타데이터 수정",
  "project.delete": "프로젝트 삭제",
  "project.duplicate": "프로젝트 복제",
  "release.preview.share": "Coogo 미리보기 공유",
  "release.verify": "배포 검증 실행",
  "release.deploy": "배포 릴리스 게시",
  "release.readback": "배포 검사 새로 고침",
  "release.rollback": "배포 영수증 롤백",
  "project.publish.confirm": "프로젝트 게시",
};

const JA: OperationLabelTable = {
  "workspace.semantic-sync": "1 つの変更を全体へ同期",
  "page.details.review": "ページを確認",
  "page.details.rename": "ページ名を変更",
  "page.details.route": "ルートを変更",
  "page.details.blocks": "ブロックを確認",
  "page.details.layout": "レイアウトを調整",
  "page.details.delete": "ページを削除",
  "element.add": "要素を追加",
  "element.update": "要素を更新",
  "element.remove": "要素を削除",
  "behavior.surface.review": "挙動を確認",
  "behavior.flow.review": "フローを確認",
  "flow.create": "フローを作成",
  "flow.rename": "フロー名を変更",
  "behavior.flow.add-step": "フローステップを追加",
  "behavior.flow.connect-selected": "選択中の要素を接続",
  "event-binding.remove": "イベントバインディングを削除",
  "behavior.flow.test": "フローをテスト",
  "behavior.flow.delete": "フローを削除",
  "data.surface.review": "データを確認",
  "evidence.checks.review": "チェックを確認",
  "evidence.candidate.read": "候補検証を読み取り",
  "evidence.sufficiency.read": "ソース充足度を読み取り",
  "evidence.sufficiency.answer": "ソース確認に回答",
  "evidence.knowledge.search": "承認済みナレッジを検索",
  "variable.add": "変数を追加",
  "variable.update": "変数を更新",
  "variable.remove": "変数を削除",
  "data.source.add": "データソースを追加",
  "data.source.scaffold-from-chat": "データソースをひな形作成",
  "datasource.update": "データソースを更新",
  "data.binding.connect": "データバインディングを接続",
  "data.flow.debug": "データフローをデバッグ",
  "data.source.remove": "データソースを削除",
  "application.generate": "アプリケーションを生成",
  "application.create-from-brief": "ブリーフからアプリケーションを作成",
  "application.patch": "アプリケーションパッチを適用",
  "application.bundle.export": "Coogo バンドルを書き出し",
  "figma.source.import": "Figma ソースを取り込み",
  "figma.token.validate": "Figma トークンを検証",
  "figma.frame.import": "Figma フレームを取り込み",
  "github.preview.import": "GitHub PR プレビューを取り込み",
  "source.figma.inspect": "Figma ソースを点検",
  "source.figma.export_original": "現在の Figma ソースを書き出し",
  "source.figma.edit": "Figma ソースを編集",
  "source.figma.apply_design_mutation": "連携デザイン変更を適用",
  "source.figma.propose_design_mutation": "アプリとソースの編集を準備",
  "source.figma.apply_mapping": "ソースマッピングを適用",
  "source.figma.decide_tokens": "デザイントークンを決定",
  "source.figma.visual_review": "ビジュアルレビューを実行",
  "source.figma.assemble": "ソースアセンブリを構築",
  "source.figma.runtime_proof": "ランタイム証明を実行",
  "source.figma.create_candidate": "Coogo 候補を作成",
  "report.workspace.review": "レポートワークスペースを確認",
  "report.document-page.review": "ドキュメントページを確認",
  "report.document-page.edit": "ドキュメントページを編集",
  "report.document-page.validate": "ドキュメントページを検証",
  "report.document-page.export": "ドキュメントページを書き出し",
  "report.export-target.validate": "書き出し先を検証",
  "document.workspace.inspect": "ドキュメントワークスペースを点検",
  "document.page.inspect": "ドキュメントページを点検",
  "document.page.create": "ドキュメントページを作成",
  "document.page.update_node": "ドキュメント内容を更新",
  "document.page.wordgard": "ナラティブ下書きを開く",
  "document.page.move": "ドキュメントページを移動",
  "document.page.delete": "ドキュメントページを削除",
  "document.page.export": "ドキュメントページを書き出し",
  "document.page.select_anchor": "ドキュメントアンカーを選択",
  "document.page.propose_edit": "ドキュメント編集を提案",
  "document.proposal.apply": "ドキュメント提案を適用",
  "document.proposal.list": "ドキュメント提案を一覧",
  "document.proposal.reject": "ドキュメント提案を却下",
  "document.blocker.explain": "ドキュメントの阻害要因を説明",
  "document.target.export": "ドキュメント書き出し先へ出力",
  "document.target.readback": "ドキュメント書き出しチェックを点検",
  "theme.preset.set": "テーマプリセットを設定",
  "theme.color.update": "テーマカラーを更新",
  "theme.font.set": "アプリのフォントを設定",
  "theme.radius.set": "アプリの角丸を設定",
  "theme.reset": "アプリのテーマをリセット",
  "project.create": "プロジェクトを作成",
  "project.open": "プロジェクトを開く",
  "project.close": "プロジェクトを閉じる",
  "project.rename": "プロジェクト名を変更",
  "project.metadata.update": "プロジェクトのメタデータを更新",
  "project.delete": "プロジェクトを削除",
  "project.duplicate": "プロジェクトを複製",
  "release.preview.share": "Coogo プレビューを共有",
  "release.verify": "公開検証を実行",
  "release.deploy": "公開リリースをデプロイ",
  "release.readback": "公開チェックを更新",
  "release.rollback": "公開レシートへロールバック",
  "project.publish.confirm": "プロジェクトを公開",
};

const ZH: OperationLabelTable = {
  "workspace.semantic-sync": "将一处改动同步到各处",
  "page.details.review": "查看页面",
  "page.details.rename": "重命名页面",
  "page.details.route": "修改路由",
  "page.details.blocks": "查看区块",
  "page.details.layout": "调整布局",
  "page.details.delete": "删除页面",
  "element.add": "添加元素",
  "element.update": "更新元素",
  "element.remove": "移除元素",
  "behavior.surface.review": "查看行为",
  "behavior.flow.review": "查看流程",
  "flow.create": "创建流程",
  "flow.rename": "重命名流程",
  "behavior.flow.add-step": "添加流程步骤",
  "behavior.flow.connect-selected": "连接所选元素",
  "event-binding.remove": "移除事件绑定",
  "behavior.flow.test": "测试流程",
  "behavior.flow.delete": "删除流程",
  "data.surface.review": "查看数据",
  "evidence.checks.review": "查看检查",
  "evidence.candidate.read": "读取候选验证",
  "evidence.sufficiency.read": "读取来源充分性",
  "evidence.sufficiency.answer": "回答来源澄清问题",
  "evidence.knowledge.search": "搜索已批准的知识",
  "variable.add": "添加变量",
  "variable.update": "更新变量",
  "variable.remove": "移除变量",
  "data.source.add": "添加数据源",
  "data.source.scaffold-from-chat": "生成数据源脚手架",
  "datasource.update": "更新数据源",
  "data.binding.connect": "连接数据绑定",
  "data.flow.debug": "调试数据流",
  "data.source.remove": "移除数据源",
  "application.generate": "生成应用",
  "application.create-from-brief": "根据简报创建应用",
  "application.patch": "应用补丁",
  "application.bundle.export": "导出 Coogo 捆绑包",
  "figma.source.import": "导入 Figma 来源",
  "figma.token.validate": "校验 Figma 令牌",
  "figma.frame.import": "导入 Figma 画板",
  "github.preview.import": "导入 GitHub PR 预览",
  "source.figma.inspect": "检查 Figma 来源",
  "source.figma.export_original": "导出当前 Figma 来源",
  "source.figma.edit": "编辑 Figma 来源",
  "source.figma.apply_design_mutation": "应用协同设计变更",
  "source.figma.propose_design_mutation": "准备应用与来源编辑",
  "source.figma.apply_mapping": "应用来源映射",
  "source.figma.decide_tokens": "确定设计令牌",
  "source.figma.visual_review": "运行视觉评审",
  "source.figma.assemble": "构建来源装配",
  "source.figma.runtime_proof": "运行运行时验证",
  "source.figma.create_candidate": "创建 Coogo 候选",
  "report.workspace.review": "查看报表工作区",
  "report.document-page.review": "查看文档页面",
  "report.document-page.edit": "编辑文档页面",
  "report.document-page.validate": "校验文档页面",
  "report.document-page.export": "导出文档页面",
  "report.export-target.validate": "校验导出目标",
  "document.workspace.inspect": "检查文档工作区",
  "document.page.inspect": "检查文档页面",
  "document.page.create": "创建文档页面",
  "document.page.update_node": "更新文档内容",
  "document.page.wordgard": "打开叙述草稿",
  "document.page.move": "移动文档页面",
  "document.page.delete": "删除文档页面",
  "document.page.export": "导出文档页面",
  "document.page.select_anchor": "选择文档锚点",
  "document.page.propose_edit": "提议文档编辑",
  "document.proposal.apply": "应用文档提案",
  "document.proposal.list": "列出文档提案",
  "document.proposal.reject": "拒绝文档提案",
  "document.blocker.explain": "说明文档阻碍项",
  "document.target.export": "导出到文档目标",
  "document.target.readback": "检查文档导出检查项",
  "theme.preset.set": "设置主题预设",
  "theme.color.update": "更新主题颜色",
  "theme.font.set": "设置应用字体",
  "theme.radius.set": "设置应用圆角",
  "theme.reset": "重置应用主题",
  "project.create": "创建项目",
  "project.open": "打开项目",
  "project.close": "关闭项目",
  "project.rename": "重命名项目",
  "project.metadata.update": "更新项目元数据",
  "project.delete": "删除项目",
  "project.duplicate": "复制项目",
  "release.preview.share": "分享 Coogo 预览",
  "release.verify": "运行发布校验",
  "release.deploy": "部署发布版本",
  "release.readback": "刷新发布检查",
  "release.rollback": "回滚发布回执",
  "project.publish.confirm": "发布项目",
};

const ZH_TW: OperationLabelTable = {
  "workspace.semantic-sync": "將一處變更同步到各處",
  "page.details.review": "檢視頁面",
  "page.details.rename": "重新命名頁面",
  "page.details.route": "變更路由",
  "page.details.blocks": "檢視區塊",
  "page.details.layout": "調整版面",
  "page.details.delete": "刪除頁面",
  "element.add": "新增元素",
  "element.update": "更新元素",
  "element.remove": "移除元素",
  "behavior.surface.review": "檢視行為",
  "behavior.flow.review": "檢視流程",
  "flow.create": "建立流程",
  "flow.rename": "重新命名流程",
  "behavior.flow.add-step": "新增流程步驟",
  "behavior.flow.connect-selected": "連接所選元素",
  "event-binding.remove": "移除事件繫結",
  "behavior.flow.test": "測試流程",
  "behavior.flow.delete": "刪除流程",
  "data.surface.review": "檢視資料",
  "evidence.checks.review": "檢視檢查",
  "evidence.candidate.read": "讀取候選驗證",
  "evidence.sufficiency.read": "讀取來源充分性",
  "evidence.sufficiency.answer": "回答來源釐清問題",
  "evidence.knowledge.search": "搜尋已核准的知識",
  "variable.add": "新增變數",
  "variable.update": "更新變數",
  "variable.remove": "移除變數",
  "data.source.add": "新增資料來源",
  "data.source.scaffold-from-chat": "建立資料來源骨架",
  "datasource.update": "更新資料來源",
  "data.binding.connect": "連接資料繫結",
  "data.flow.debug": "偵錯資料流程",
  "data.source.remove": "移除資料來源",
  "application.generate": "產生應用程式",
  "application.create-from-brief": "依簡報建立應用程式",
  "application.patch": "套用應用程式修補",
  "application.bundle.export": "匯出 Coogo 套件",
  "figma.source.import": "匯入 Figma 來源",
  "figma.token.validate": "驗證 Figma 權杖",
  "figma.frame.import": "匯入 Figma 框架",
  "github.preview.import": "匯入 GitHub PR 預覽",
  "source.figma.inspect": "檢查 Figma 來源",
  "source.figma.export_original": "匯出目前的 Figma 來源",
  "source.figma.edit": "編輯 Figma 來源",
  "source.figma.apply_design_mutation": "套用協同設計變更",
  "source.figma.propose_design_mutation": "準備應用與來源編輯",
  "source.figma.apply_mapping": "套用來源對應",
  "source.figma.decide_tokens": "決定設計權杖",
  "source.figma.visual_review": "執行視覺審查",
  "source.figma.assemble": "建置來源組裝",
  "source.figma.runtime_proof": "執行執行階段驗證",
  "source.figma.create_candidate": "建立 Coogo 候選",
  "report.workspace.review": "檢視報表工作區",
  "report.document-page.review": "檢視文件頁面",
  "report.document-page.edit": "編輯文件頁面",
  "report.document-page.validate": "驗證文件頁面",
  "report.document-page.export": "匯出文件頁面",
  "report.export-target.validate": "驗證匯出目標",
  "document.workspace.inspect": "檢查文件工作區",
  "document.page.inspect": "檢查文件頁面",
  "document.page.create": "建立文件頁面",
  "document.page.update_node": "更新文件內容",
  "document.page.wordgard": "開啟敘述草稿",
  "document.page.move": "移動文件頁面",
  "document.page.delete": "刪除文件頁面",
  "document.page.export": "匯出文件頁面",
  "document.page.select_anchor": "選擇文件錨點",
  "document.page.propose_edit": "提議文件編輯",
  "document.proposal.apply": "套用文件提案",
  "document.proposal.list": "列出文件提案",
  "document.proposal.reject": "拒絕文件提案",
  "document.blocker.explain": "說明文件阻礙項",
  "document.target.export": "匯出至文件目標",
  "document.target.readback": "檢查文件匯出檢查項",
  "theme.preset.set": "設定主題預設集",
  "theme.color.update": "更新主題色彩",
  "theme.font.set": "設定應用字型",
  "theme.radius.set": "設定應用圓角",
  "theme.reset": "重設應用主題",
  "project.create": "建立專案",
  "project.open": "開啟專案",
  "project.close": "關閉專案",
  "project.rename": "重新命名專案",
  "project.metadata.update": "更新專案中繼資料",
  "project.delete": "刪除專案",
  "project.duplicate": "複製專案",
  "release.preview.share": "分享 Coogo 預覽",
  "release.verify": "執行發佈驗證",
  "release.deploy": "部署發佈版本",
  "release.readback": "重新整理發佈檢查",
  "release.rollback": "回復發佈收據",
  "project.publish.confirm": "發佈專案",
};

export const STUDIO_OPERATION_LABEL_COPY: Record<
  LocaleCode,
  OperationLabelTable
> = { en: EN, ko: KO, ja: JA, zh: ZH, "zh-TW": ZH_TW };

/**
 * The operator-facing label for one operation, or `undefined` when the id is
 * not a Studio chat operation. Callers that already hold the registry contract
 * should prefer `resolveStudioOperationLabel`, which falls back to the
 * contract's English label rather than rendering nothing.
 */
export function getStudioOperationLabel(
  operationId: string | undefined,
  locale: LocaleCode,
): string | undefined {
  if (!operationId) return undefined;
  const table = STUDIO_OPERATION_LABEL_COPY[locale] ?? EN;
  return (
    table[operationId as StudioOperationId] ??
    EN[operationId as StudioOperationId]
  );
}

/**
 * The label a render sink should show. Falls back to the registry's English
 * label (the prompt-channel value) when an operation has no display entry yet,
 * so a new operation renders readable English instead of a blank or an id.
 *
 * Synchronous, and must stay synchronous — see the module header.
 */
export function resolveStudioOperationLabel(
  operationId: string | undefined,
  locale: LocaleCode,
  fallbackLabel: string,
): string {
  return getStudioOperationLabel(operationId, locale) ?? fallbackLabel;
}

/**
 * The description a render sink should show, fetched on demand.
 *
 * Async because `studio-operation-description-copy.ts` is a dynamic import;
 * the sole production caller is a click handler. Same fallback rule as the
 * label: an id with no entry yields the registry's English description.
 */
export async function loadStudioOperationDescription(
  operationId: string | undefined,
  locale: LocaleCode,
  fallbackDescription: string,
): Promise<string> {
  const { getStudioOperationDescription } = await import(
    "./studio-operation-description-copy"
  );
  return getStudioOperationDescription(operationId, locale) ?? fallbackDescription;
}

/**
 * The label a BUTTON should show when its caller also passed its own text.
 *
 * `AskCogoSurfaceButton` takes a `label` prop, and three call sites deliberately
 * word it more tightly than the operation is named in the registry:
 *
 *   source.figma.inspect                  "Inspect source"    vs "Inspect Figma source"
 *   source.figma.visual_review            "Visual review"     vs "Run visual review"
 *   source.figma.propose_design_mutation  "Edit app + source" vs "Prepare app + source edit"
 *
 * That wording belongs to the calling surface, so it wins. Substituting the
 * registry name for it would have changed what an ENGLISH operator reads, which
 * is a behaviour change wearing translation's clothes — it was caught by
 * `ask-cogo-surface-button.test.tsx` when this module first tried it.
 *
 * So: localize only when the caller is echoing the operation's own English
 * name. A caller with its own wording keeps it verbatim, in every locale, until
 * the surface that owns that string localizes its own prop.
 */
export function resolveStudioOperationButtonLabel(
  operationId: string | undefined,
  callerLabel: string,
  locale: LocaleCode,
): string {
  const english = getStudioOperationLabel(operationId, "en");
  if (!english || english !== callerLabel) return callerLabel;
  return resolveStudioOperationLabel(operationId, locale, callerLabel);
}

/** React binding for the render sinks that live inside components. */
export function useStudioOperationDisplayCopy() {
  const { locale } = useStudioLocale();
  return {
    locale,
    label: (operationId: string | undefined, fallbackLabel: string) =>
      resolveStudioOperationLabel(operationId, locale, fallbackLabel),
    buttonLabel: (operationId: string | undefined, callerLabel: string) =>
      resolveStudioOperationButtonLabel(operationId, callerLabel, locale),
    loadDescription: (
      operationId: string | undefined,
      fallbackDescription: string,
    ) => loadStudioOperationDescription(operationId, locale, fallbackDescription),
  };
}

/**
 * Frame sentences for the notice `ask-cogo-surface-button.tsx` pushes when an
 * operation is attached to chat. The operation's own label and description come
 * from the tables above; these are the words around them.
 */
export interface StudioOperationNoticeCopy {
  connected: (label: string) => string;
  willConfirm: string;
  readOnly: string;
}

const NOTICE: Record<LocaleCode, StudioOperationNoticeCopy> = {
  en: {
    connected: (label) => `${label} is connected to chat.`,
    willConfirm: "Coogo will ask for confirmation before applying changes.",
    readOnly: "This review does not change the workspace.",
  },
  ko: {
    connected: (label) => `${label} 작업이 채팅에 연결되었습니다.`,
    willConfirm: "Coogo가 변경 사항을 적용하기 전에 확인을 요청합니다.",
    readOnly: "이 검토는 작업 공간을 변경하지 않습니다.",
  },
  ja: {
    connected: (label) => `${label} がチャットに接続されました。`,
    willConfirm: "Coogo は変更を適用する前に確認を求めます。",
    readOnly: "このレビューはワークスペースを変更しません。",
  },
  zh: {
    connected: (label) => `${label} 已连接到聊天。`,
    willConfirm: "Coogo 会在应用更改之前请求确认。",
    readOnly: "此次查看不会更改工作区。",
  },
  "zh-TW": {
    connected: (label) => `${label} 已連接到聊天。`,
    willConfirm: "Coogo 會在套用變更之前請求確認。",
    readOnly: "此次檢視不會變更工作區。",
  },
};

export function getStudioOperationNoticeCopy(
  locale: LocaleCode,
): StudioOperationNoticeCopy {
  return NOTICE[locale] ?? NOTICE.en;
}
